import { ChevronDown, ChevronRight, Database, Download, ExternalLink, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState } from '../../../components/EmptyState.js';
import { GroupedListSkeleton } from '../../../components/Skeletons.js';
import { Button } from '../../../components/ui/button.js';
import { Input } from '../../../components/ui/input.js';
import { useAuth } from '../../../lib/auth-context.js';
import { isCustomerAdmin } from '../../../lib/roles.js';
import { cn } from '../../../lib/utils.js';
import { useWorkfrontObjectCatalog } from '../hooks.js';

/**
 * Workfront Object Catalog. Mirrors the legacy
 * `wf-audit/src/slices/workfront/pages/object-catalog.tsx`:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Custom Form 18 | Document 6 | Portfolio 4 | … (tabs)         │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ [search]                                       [Export CSV]  │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ ▸ Object name           Workfront ID    (N scenarios)        │
 *   │ ▾ Object name           Workfront ID    (N scenarios)        │
 *   │     ↳ scenario name  · fusion module name              Open  │
 *   │     ↳ scenario name  · fusion module name              Open  │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ Rows per page <10|25|50>          Page X of Y  ← →           │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Operator-confirmed: each object row is expandable; expanding shows
 * the list of Fusion scenarios that use the object.
 *
 * Read-only today. Bulk Workfront mutations are out of scope for
 * this catalog; the migration wizard sibling page handles the one
 * mutate case (form clone / replace) that maps cleanly to a single
 * Workfront PUT.
 */
interface FusionLink {
  fusionModuleType: string;
  fusionModuleName: string;
  fusionScenarioFusionId: string;
  scenarioName: string;
  scenarioUrl: string | null;
}

interface CatalogItem {
  workfrontId: string;
  workfrontType: string | null;
  workfrontTypeLabel: string | null;
  workfrontName: string | null;
  workfrontLink: string | null;
  fusionLinks: ReadonlyArray<FusionLink>;
}

const UNKNOWN_GROUP = 'Unidentified or Deleted';
const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

export function WorkfrontObjectCatalogPage(): JSX.Element {
  const { session } = useAuth();
  const { data: rows = [], isLoading, error } = useWorkfrontObjectCatalog();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageIndex, setPageIndex] = useState<number>(0);

  const items = useMemo<ReadonlyArray<CatalogItem>>(
    () =>
      rows.map((r) => ({
        workfrontId: typeof r.workfrontId === 'string' ? r.workfrontId : '',
        workfrontType: typeof r.workfrontType === 'string' ? r.workfrontType : null,
        workfrontTypeLabel: typeof r.workfrontTypeLabel === 'string' ? r.workfrontTypeLabel : null,
        workfrontName: typeof r.workfrontName === 'string' ? r.workfrontName : null,
        workfrontLink: typeof r.workfrontLink === 'string' ? r.workfrontLink : null,
        fusionLinks: Array.isArray(r.fusionLinks)
          ? (r.fusionLinks as ReadonlyArray<FusionLink>)
          : [],
      })),
    [rows],
  );

  // Search-narrowed before tab-filtering — type tabs always show the
  // count of items in that type that match the active search query.
  const searchFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.workfrontId.toLowerCase().includes(q) ||
        (it.workfrontName ?? '').toLowerCase().includes(q) ||
        (it.workfrontTypeLabel ?? '').toLowerCase().includes(q) ||
        it.fusionLinks.some(
          (l) =>
            l.scenarioName.toLowerCase().includes(q) ||
            l.fusionModuleName.toLowerCase().includes(q),
        ),
    );
  }, [items, search]);

  // Tab definitions — one entry per `workfrontTypeLabel`. Sorted by
  // label, with the catch-all `Unidentified or Deleted` pinned last.
  const tabs = useMemo(() => {
    const map = new Map<string, number>();
    for (const it of searchFiltered) {
      const title = it.workfrontTypeLabel ?? it.workfrontType ?? UNKNOWN_GROUP;
      map.set(title, (map.get(title) ?? 0) + 1);
    }
    return [...map.entries()]
      .map(([title, count]) => ({ title, count }))
      .sort((a, b) => {
        if (a.title === UNKNOWN_GROUP) return 1;
        if (b.title === UNKNOWN_GROUP) return -1;
        return a.title.localeCompare(b.title);
      });
  }, [searchFiltered]);

  // Default the active tab to the first non-empty type once data loads,
  // and re-pick if the active tab disappears under a new search.
  useEffect(() => {
    if (tabs.length === 0) {
      if (activeTab !== null) setActiveTab(null);
      return;
    }
    const hit = tabs.find((t) => t.title === activeTab);
    if (!hit) setActiveTab(tabs[0]!.title);
  }, [tabs, activeTab]);

  // Active-tab items (after search). Sorted alphabetically by name
  // for stable pagination.
  const activeItems = useMemo(() => {
    if (!activeTab) return [];
    return searchFiltered
      .filter((it) => (it.workfrontTypeLabel ?? it.workfrontType ?? UNKNOWN_GROUP) === activeTab)
      .sort((a, b) => (a.workfrontName ?? '').localeCompare(b.workfrontName ?? ''));
  }, [searchFiltered, activeTab]);

  // Reset pagination when the filtered set shifts (new search, new tab).
  useEffect(() => {
    setPageIndex(0);
  }, [activeTab, search, pageSize]);

  const totalPages = Math.max(1, Math.ceil(activeItems.length / pageSize));
  const pageItems = useMemo(
    () => activeItems.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [activeItems, pageIndex, pageSize],
  );

  const toggleRow = (id: string): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  function handleExportCsv(): void {
    // Flatten the active-tab + active-search set into one row per
    // (object × fusion-link). Objects with zero links emit one row
    // with empty scenario columns so they still show up in the export.
    const rowsOut: string[] = [
      [
        'Workfront Type',
        'Workfront ID',
        'Workfront Name',
        'Workfront Link',
        'Scenario',
        'Fusion Module',
        'Scenario Fusion ID',
      ]
        .map(csvCell)
        .join(','),
    ];
    for (const it of activeItems) {
      const type = it.workfrontTypeLabel ?? it.workfrontType ?? '';
      if (it.fusionLinks.length === 0) {
        rowsOut.push(
          [type, it.workfrontId, it.workfrontName ?? '', it.workfrontLink ?? '', '', '', '']
            .map(csvCell)
            .join(','),
        );
        continue;
      }
      for (const link of it.fusionLinks) {
        rowsOut.push(
          [
            type,
            it.workfrontId,
            it.workfrontName ?? '',
            it.workfrontLink ?? '',
            link.scenarioName,
            link.fusionModuleName,
            link.fusionScenarioFusionId,
          ]
            .map(csvCell)
            .join(','),
        );
      }
    }
    const blob = new Blob([rowsOut.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workfront-objects-${(activeTab ?? 'all').toLowerCase().replace(/\s+/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const totalCount = items.length;
  const filteredCount = searchFiltered.length;

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="flex flex-wrap items-center justify-between gap-3 px-6 pb-2 pt-6">
        <div>
          <h1 className="text-2xl font-light text-foreground">Workfront Objects in Fusion</h1>
          <p className="text-sm text-foreground/60">
            Every Workfront object referenced by any Fusion scenario in this license.
          </p>
        </div>
        {isCustomerAdmin(session) ? (
          <Link
            to="/workfront/custom-forms-migration"
            className="text-sm text-foreground/70 underline hover:text-foreground"
          >
            Custom forms migration →
          </Link>
        ) : null}
      </header>

      {/* Tab row — one entry per Workfront type with count badge. */}
      {tabs.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-foreground/10 bg-foreground/[0.03] px-6 py-2">
          {tabs.map((t) => {
            const active = t.title === activeTab;
            return (
              <button
                key={t.title}
                type="button"
                onClick={() => setActiveTab(t.title)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors',
                  active
                    ? 'border-foreground/40 bg-foreground/10 text-foreground'
                    : 'border-foreground/15 bg-transparent text-foreground/65 hover:border-foreground/30 hover:text-foreground',
                )}
              >
                <span>{t.title}</span>
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] font-mono',
                    active
                      ? 'bg-foreground/15 text-foreground'
                      : 'bg-foreground/10 text-foreground/55',
                  )}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3 border-b border-foreground/10 bg-foreground/[0.03] px-6 py-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/40" />
          <Input
            type="search"
            placeholder="Search ID, name, type, or scenario..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-foreground/15 bg-foreground/5 pl-9 text-foreground placeholder:text-foreground/40 focus-visible:ring-white/30"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleExportCsv}
          disabled={activeItems.length === 0}
          className="border-foreground/15 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
          title="Export the visible objects + their Fusion scenario links to a CSV file"
        >
          <Download className="mr-1.5 h-3.5 w-3.5" />
          Export CSV
        </Button>
        <span className="ml-auto text-xs text-foreground/50">
          {filteredCount} of {totalCount} object{totalCount === 1 ? '' : 's'} matched
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        {error ? (
          <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
            Could not load object catalog: {error.message}
          </div>
        ) : isLoading ? (
          <GroupedListSkeleton groups={3} itemsPerGroup={2} />
        ) : activeItems.length === 0 ? (
          totalCount === 0 ? (
            <EmptyState
              icon={Database}
              tint="sky"
              title="No Workfront objects detected yet"
              description="Run an audit pipeline in this license — objects referenced by any Fusion scenario will land here as the engine catalog populates."
            />
          ) : (
            <EmptyState
              icon={Search}
              title="No objects match your search"
              description={
                <>
                  {totalCount} object{totalCount === 1 ? '' : 's'} hidden — try clearing the search
                  box or switching to a different type tab.
                </>
              }
            />
          )
        ) : (
          <div className="space-y-1.5">
            {pageItems.map((item) => {
              const expanded = expandedRows.has(item.workfrontId);
              const scenarioCount = item.fusionLinks.length;
              return (
                <article
                  key={item.workfrontId}
                  className="rounded-md border border-foreground/10 bg-foreground/[0.02]"
                >
                  <button
                    type="button"
                    onClick={() => toggleRow(item.workfrontId)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-foreground/[0.04]"
                  >
                    {expanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-foreground/40" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-foreground/40" />
                    )}
                    <span className="flex-1 truncate text-sm font-medium text-foreground">
                      {item.workfrontName || '(unnamed)'}
                    </span>
                    {item.workfrontLink ? (
                      <a
                        href={item.workfrontLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 font-mono text-xs text-blue-700 underline hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200"
                      >
                        {item.workfrontId}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="font-mono text-xs text-foreground/55">
                        {item.workfrontId}
                      </span>
                    )}
                    <span className="rounded bg-foreground/10 px-1.5 py-0.5 text-[11px] text-foreground/65">
                      {scenarioCount} scenario{scenarioCount === 1 ? '' : 's'}
                    </span>
                  </button>
                  {expanded ? (
                    <div className="border-t border-foreground/10 px-3 py-2">
                      {scenarioCount === 0 ? (
                        <p className="text-xs italic text-foreground/45">
                          No Fusion scenarios reference this object.
                        </p>
                      ) : (
                        <ul className="space-y-1">
                          {item.fusionLinks.map((link, i) => (
                            <li
                              key={`${link.fusionScenarioFusionId}-${i}`}
                              className="flex items-center gap-2 text-xs"
                            >
                              <Link
                                to={`/scenarios?scenarioId=${encodeURIComponent(link.fusionScenarioFusionId)}`}
                                className="flex-1 truncate text-foreground hover:underline"
                                title={link.scenarioName}
                              >
                                {link.scenarioName}
                              </Link>
                              <span className="shrink-0 text-foreground/45">
                                · {link.fusionModuleName}
                              </span>
                              {link.scenarioUrl ? (
                                <a
                                  href={link.scenarioUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex shrink-0 items-center gap-1 text-blue-700 underline hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200"
                                  title="Open the scenario in Fusion"
                                >
                                  Open
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination row */}
      {activeItems.length > 0 ? (
        <div className="flex items-center justify-end gap-4 border-t border-foreground/10 bg-foreground/[0.03] px-6 py-2 text-xs text-foreground/65">
          <label className="inline-flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="rounded-md border border-foreground/15 bg-foreground/5 px-1.5 py-0.5 text-foreground"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <span>
            Page {pageIndex + 1} of {totalPages}
          </span>
          <div className="inline-flex">
            <button
              type="button"
              onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              disabled={pageIndex === 0}
              aria-label="Previous page"
              className="rounded-l-md border border-foreground/15 px-2 py-0.5 transition-colors hover:bg-foreground/10 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronRight className="h-3.5 w-3.5 rotate-180" />
            </button>
            <button
              type="button"
              onClick={() => setPageIndex((i) => Math.min(totalPages - 1, i + 1))}
              disabled={pageIndex >= totalPages - 1}
              aria-label="Next page"
              className="rounded-r-md border border-l-0 border-foreground/15 px-2 py-0.5 transition-colors hover:bg-foreground/10 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Standard CSV-cell escape: wrap in double-quotes when the cell
 * contains a comma, double-quote, or newline; double-up internal
 * double-quotes so a reader (Excel / pandas / csv module) parses it
 * back cleanly.
 */
function csvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
